import {
  ClassEnrichedWithUserInfo,
  ClassI as ClassView,
  TeacherClassViewObject
} from '@seneca/class-service-types';
import { Client, ClientOptions, Fetch } from '@seneca/client-library';
import { NotFoundError } from '@seneca/errors';
import { errorCodes } from '@seneca/schools-service-consts';
import {
  BatchSaveUserSchoolsResponse,
  ClassEntity,
  ClassI,
  InvalidStudentI,
  InvalidTeacherI,
  QuerySchoolsResponse,
  SaveSchoolByExternalIdResponse,
  SaveSchoolResponse,
  SchoolEntityEnrichedWithParentInfo,
  SchoolEntityI,
  SchoolEntityParentCounts,
  SchoolEntityTypes,
  SchoolInfoViewResponse,
  SchoolParentConnectionsInfoResponse,
  StudentI,
  TeacherI,
  UserSchoolI
} from '@seneca/schools-service-types';

import BadRequestError from './errors/BadRequestError';
import BatchFetchEntitiesByExternalIdDoesNotExistError from './errors/BatchFetchEntitiesByExternalIdDoesNotExistError';
import ForbiddenAccessToSchoolEntitiesError from './errors/ForbiddenAccessToSchoolEntitiesError';
import ForbiddenForSchoolError from './errors/ForbiddenForSchoolError';
import SchoolInfoBySchoolIdDoesNotExistError from './errors/SchoolInfoBySchoolIdDoesNotExistError';
import SchoolInfoByUrnDoesNotExistError from './errors/SchoolInfoByUrnDoesNotExistError';
import SchoolNotFoundError from './errors/SchoolNotFoundError';
import SchoolSyncExpiredError from './errors/SchoolSyncExpiredError';
import UserSchoolInfoDoesNotExistError from './errors/UserSchoolInfoDoesNotExistError';

/**
 * SchoolsServiceClient
 * @param {Object} options options object input data and dependency injection
 * @param {Object} options.fetch change fetch method from whatwg-fetch (optional, default: whatwg-fetch)
 * @param {string} options.courseId saves entering courseId required for some methods
 * @param {string} options.protocol the http protocol of the service endpoint
 * @param {string} options.host the host of the service endpoint
 * @param {string} options.path the path of the service endpoint (optional)
 * @param {number} options.retries the number of times the client should retry on network error / service error (optional, default: 3)
 * @param {number} options.delayInSecs the delay between retries (optional, default: 2)
 * @param {Object} options.credentials identifiers and secrets required to hit protected endpoints (optional)
 * @param {string} options.region states the region of the service for signing (optional, default: eu-west-1)
 * @param {RequestMaker} options.requestMaker for overriding request making logic (optional, default: RequestMaker)
 * @throws {UrlConstructionError} when either options.protocol or options.host is missing
 */
export default class SchoolsServiceClient extends Client {
  constructor(fetch: Fetch, options: ClientOptions) {
    super(fetch, options);
  }

  /**
   * @param {string} searchTerm - search term to query schools by
   * @throws {UnexpectedServerResponse} when service returns an unexpected response
   * @throws {RequestLimitExceeded} when the max number of retries has been hit
   * @returns {Promise.schools}
   */
  async searchSchoolsList(searchTerm: string) {
    let url = this._constructUrl('api/schools');

    if (searchTerm) {
      url += `?searchTerm=${encodeURIComponent(searchTerm)}`;
    }

    const expectedResponses = [200];
    const response = await this.requestMaker.makeRequest<QuerySchoolsResponse>(
      url,
      { method: 'GET' },
      expectedResponses
    );

    const body = response.json;
    return body?.items;
  }

  async fetchMySchoolInfo() {
    const url = this._constructUrl(`api/school-info/me`);
    const expectedResponses = [200, 404];

    const response =
      await this.requestMaker.makeRequest<SchoolInfoViewResponse>(
        url,
        { method: 'GET' },
        expectedResponses
      );

    if (response.status === 404) {
      throw new UserSchoolInfoDoesNotExistError();
    }

    return response.json;
  }

  async putMySchoolInfo({ schoolId }: { schoolId?: string }) {
    const url = this._constructUrl(`api/school-info/me`);
    const expectedResponses = [200, 422];

    const response =
      await this.requestMaker.makeRequest<SchoolInfoViewResponse>(
        url,
        { method: 'PUT', body: JSON.stringify({ schoolId }) },
        expectedResponses
      );

    if (response.status === 422) {
      throw new SchoolInfoBySchoolIdDoesNotExistError(schoolId!);
    }

    return response.json;
  }

  async fetchSchoolInfo(schoolId: string) {
    const url = this._constructUrl(`api/school-info/${schoolId}`);
    const expectedResponses = [200, 404];

    const response =
      await this.requestMaker.makeRequest<SchoolInfoViewResponse>(
        url,
        { method: 'GET' },
        expectedResponses
      );

    if (response.status === 404) {
      throw new SchoolInfoBySchoolIdDoesNotExistError(schoolId);
    }

    return response.json;
  }

  async fetchSchoolInfoByUrn(urn: string) {
    const url = this._constructUrl(`api/admin/school-info/urn/${urn}`);
    const expectedResponses = [200, 404];

    const response =
      await this.requestMaker.makeRequest<SchoolInfoViewResponse>(
        url,
        { method: 'GET' },
        expectedResponses
      );

    if (response.status === 404) {
      throw new SchoolInfoByUrnDoesNotExistError(urn);
    }

    return response.json;
  }

  async batchSaveUserSchools(userSchools: UserSchoolI[]) {
    const url = this._constructUrl(`api/admin/school-info/users`);
    const expectedResponses = [200];

    const response =
      await this.requestMaker.makeRequest<BatchSaveUserSchoolsResponse>(
        url,
        { method: 'POST', body: JSON.stringify({ userSchools }) },
        expectedResponses
      );

    return response.json;
  }

  async fetchUserSchoolInfo(userId: string) {
    const url = this._constructUrl(`api/admin/school-info/users/${userId}`);
    const expectedResponses = [200, 404];

    const response =
      await this.requestMaker.makeRequest<SchoolInfoViewResponse>(
        url,
        { method: 'GET' },
        expectedResponses
      );

    if (response.status === 404) {
      throw new UserSchoolInfoDoesNotExistError();
    }

    return response.json;
  }

  async fetchMyTeacherSchools(args?: {
    excludeParentInfo: false;
  }): Promise<SchoolEntityEnrichedWithParentInfo[]>;
  async fetchMyTeacherSchools(args: {
    excludeParentInfo: true;
  }): Promise<SchoolEntityI[]>;
  async fetchMyTeacherSchools(args?: {
    excludeParentInfo: boolean;
  }): Promise<SchoolEntityEnrichedWithParentInfo[] | SchoolEntityI[]> {
    const url = this._constructUrl(
      'api/teacher/schools/me',
      args?.excludeParentInfo ? { excludeParentInfo: true } : undefined
    );

    const expectedResponses = [200];
    const response = await this.requestMaker.makeRequest<{
      items: SchoolEntityEnrichedWithParentInfo[] | SchoolEntityI[];
    }>(url, { method: 'GET' }, expectedResponses);

    const body = response.json!;
    return body.items;
  }

  async fetchMyStudentSchools(): Promise<SchoolEntityI[]> {
    const url = this._constructUrl('api/student/schools/me');

    const expectedResponses = [200];
    const response = await this.requestMaker.makeRequest<{
      items: SchoolEntityI[];
    }>(url, { method: 'GET' }, expectedResponses);

    const body = response.json!;
    return body.items;
  }

  async getAllTeacherClasses() {
    const url = this._constructUrl(`api/teachers/classes`);

    const expectedResponses = [200];
    const response = await this.requestMaker.makeRequest<{
      items: ClassView[];
    }>(url, { method: 'GET' }, expectedResponses);

    const body = response.json!;
    return body.items;
  }

  async getSchoolClassesAdmin(schoolId: string, teacherId: string) {
    const url = this._constructUrl(
      `api/admin/schools/${schoolId}/classes?teacherId=${teacherId}`
    );

    const expectedResponses = [200];
    const response = await this.requestMaker.makeRequest<{
      items: ClassEnrichedWithUserInfo[];
    }>(url, { method: 'GET' }, expectedResponses);

    const body = response.json!;
    return body.items;
  }

  async getSchoolClassesTeacher(
    schoolId: string,
    { limit = 1000 }: { limit?: number } = {}
  ) {
    let classes: ClassEnrichedWithUserInfo[] = [];
    let lastKey: string | undefined;

    do {
      const page = await this.fetchPageOfSchoolClassesTeacher(schoolId, {
        lastKey,
        limit
      });
      classes = classes.concat(page.items);
      lastKey = page.lastKey;
    } while (lastKey);

    return classes;
  }

  async fetchSchoolConnectionInfo(schoolId: string, fromDate: string) {
    const url = this._constructUrl(
      `api/schools/${schoolId}/reports/parent-connections`,
      {
        fromDate
      }
    );

    const expectedResponses = [200, 403];

    const response =
      await this.requestMaker.makeRequest<SchoolParentConnectionsInfoResponse>(
        url,
        { method: 'GET' },
        expectedResponses
      );

    if (response.status === 403) {
      const body = response.json as any as {
        reason: string;
      };

      if (body.reason === errorCodes.NO_ACCESS_TO_SCHOOL) {
        throw new ForbiddenAccessToSchoolEntitiesError(schoolId);
      }

      if (body.reason === errorCodes.SCHOOL_SYNC_EXPIRED) {
        throw new SchoolSyncExpiredError(schoolId);
      }
    }

    return response.json!;
  }

  async fetchSchoolParentCounts(schoolId: string) {
    const url = this._constructUrl(`api/schools/${schoolId}/parent-counts`);

    const expectedResponses = [200, 403];

    const response =
      await this.requestMaker.makeRequest<SchoolEntityParentCounts>(
        url,
        { method: 'GET' },
        expectedResponses
      );

    if (response.status === 403) {
      throw new ForbiddenAccessToSchoolEntitiesError(schoolId);
    }

    return response.json!;
  }

  async getSchoolTeachers(
    schoolId: string,
    { limit = 1000 }: { limit?: number } = {}
  ) {
    let teachers: TeacherI[] = [];
    let lastKey: string | undefined;

    do {
      const page = await this.fetchPageOfSchoolUserEntities<TeacherI>(
        SchoolEntityTypes.Teacher
      )(schoolId, {
        lastKey,
        limit
      });
      teachers = teachers.concat(page.items);
      lastKey = page.lastKey;
    } while (lastKey);

    return teachers;
  }

  async getSchoolStudents(
    schoolId: string,
    { limit = 1000 }: { limit?: number } = {}
  ) {
    let students: StudentI[] = [];
    let lastKey: string | undefined;

    do {
      const page = await this.fetchPageOfSchoolUserEntities<StudentI>(
        SchoolEntityTypes.Student
      )(schoolId, {
        lastKey,
        limit
      });
      students = students.concat(page.items);
      lastKey = page.lastKey;
    } while (lastKey);

    return students;
  }

  async getSchoolInvalidTeachers(
    schoolId: string,
    { limit = 1000 }: { limit?: number } = {}
  ) {
    let invalidTeachers: InvalidTeacherI[] = [];
    let lastKey: string | undefined;

    do {
      const page = await this.fetchPageOfSchoolUserEntities<InvalidTeacherI>(
        SchoolEntityTypes.InvalidTeacher
      )(schoolId, {
        lastKey,
        limit
      });
      invalidTeachers = invalidTeachers.concat(page.items);
      lastKey = page.lastKey;
    } while (lastKey);

    return invalidTeachers;
  }

  async getSchoolInvalidStudents(
    schoolId: string,
    { limit = 1000 }: { limit?: number } = {}
  ) {
    let invalidStudents: InvalidStudentI[] = [];
    let lastKey: string | undefined;

    do {
      const page = await this.fetchPageOfSchoolUserEntities<InvalidStudentI>(
        SchoolEntityTypes.InvalidStudent
      )(schoolId, {
        lastKey,
        limit
      });
      invalidStudents = invalidStudents.concat(page.items);
      lastKey = page.lastKey;
    } while (lastKey);

    return invalidStudents;
  }

  async fetchPageOfSchoolClassesTeacher(
    schoolId: string,
    { limit, lastKey }: { limit?: number; lastKey?: string }
  ) {
    const url = this._constructUrl(`api/schools/${schoolId}/classes`, {
      limit,
      lastKey
    });

    const expectedResponses = [200];
    const response = await this.requestMaker.makeRequest<{
      items: ClassEnrichedWithUserInfo[];
      lastKey?: string;
    }>(url, { method: 'GET' }, expectedResponses);

    return response.json!;
  }

  getSchoolUserEntitiesFetchUrlEnding(type: SchoolEntityTypes) {
    switch (type) {
      case SchoolEntityTypes.Student:
        return 'students';
      case SchoolEntityTypes.Teacher:
        return 'teachers';
      case SchoolEntityTypes.InvalidStudent:
        return 'students/invalid';
      case SchoolEntityTypes.InvalidTeacher:
        return 'teachers/invalid';
      default:
        throw new Error('Invalid school user entity type');
    }
  }

  fetchPageOfSchoolUserEntities<T>(type: SchoolEntityTypes) {
    return async (
      schoolId: string,
      { limit, lastKey }: { limit?: number; lastKey?: string }
    ) => {
      const url = this._constructUrl(
        `api/schools/${schoolId}/${this.getSchoolUserEntitiesFetchUrlEnding(
          type
        )}`,
        {
          limit,
          lastKey
        }
      );

      const expectedResponses = [200, 403];

      const response = await this.requestMaker.makeRequest<{
        items: T[];
        lastKey?: string;
      }>(url, { method: 'GET' }, expectedResponses);

      if (response.status === 403) {
        throw new ForbiddenAccessToSchoolEntitiesError(schoolId);
      }

      return response.json!;
    };
  }

  async joinSchoolClass({
    schoolId,
    classId
  }: {
    schoolId: string;
    classId: string;
  }) {
    const url = this._constructUrl(`api/schools/joinClass`);

    const expectedResponses = [200, 403];

    const response =
      await this.requestMaker.makeRequest<ClassEnrichedWithUserInfo>(
        url,
        {
          method: 'POST',
          body: JSON.stringify({ schoolId, classId })
        },
        expectedResponses
      );

    if (response.status === 403) {
      throw new ForbiddenForSchoolError(schoolId);
    }

    return response.json!;
  }

  async joinSchoolClasses({
    schoolId,
    classIds
  }: {
    schoolId: string;
    classIds: string[];
  }) {
    const url = this._constructUrl(`api/schools/joinClasses`);

    const expectedResponses = [200, 403];

    const response = await this.requestMaker.makeRequest<
      TeacherClassViewObject[]
    >(
      url,
      {
        method: 'POST',
        body: JSON.stringify({ schoolId, classIds })
      },
      expectedResponses
    );

    if (response.status === 403) {
      throw new ForbiddenForSchoolError(schoolId);
    }

    return response.json!;
  }

  async inviteSchoolParents(schoolId: string, yearGroups?: number[]) {
    const url = this._constructUrl(`api/schools/${schoolId}/invite-parents`);

    const expectedResponses = [204, 400, 403];

    const response = await this.requestMaker.makeRequest(
      url,
      {
        method: 'post',
        ...(!!yearGroups && { body: JSON.stringify({ yearGroups }) })
      },
      expectedResponses
    );

    if (response.status === 403) {
      throw new ForbiddenForSchoolError(schoolId);
    }

    if (response.status === 400) {
      const body = response.json as unknown as {
        pointer: string;
        reason: string;
      };
      throw new BadRequestError(body.pointer, body.reason);
    }

    return response.json!;
  }

  async inviteSchoolParentsForClasses(schoolId: string, classIds: string[]) {
    const url = this._constructUrl(
      `api/schools/${schoolId}/invite-classes-parents`
    );

    const expectedResponses = [204, 403, 404];

    const response = await this.requestMaker.makeRequest(
      url,
      {
        method: 'POST',
        body: JSON.stringify({ classIds })
      },
      expectedResponses
    );

    if (response.status === 403) {
      throw new ForbiddenForSchoolError(schoolId);
    }

    if (response.status === 404) {
      throw new NotFoundError();
    }

    return response.json!;
  }

  async adminInviteSchoolParentsByExternalId(
    externalId: string,
    yearGroups?: number[]
  ) {
    const url = this._constructUrl(
      `api/admin/schools/external/${externalId}/invite-parents`
    );

    const expectedResponses = [204, 400];

    const response = await this.requestMaker.makeRequest(
      url,
      {
        method: 'post',
        ...(!!yearGroups && { body: JSON.stringify({ yearGroups }) })
      },
      expectedResponses
    );

    if (response.status === 400) {
      const body = response.json as unknown as {
        pointer: string;
        reason: string;
      };
      throw new BadRequestError(body.pointer, body.reason);
    }

    return response.json!;
  }

  async batchResetUserPasswords({
    schoolId,
    userIds,
    newPassword
  }: {
    schoolId: string;
    userIds: string[];
    newPassword: string;
  }) {
    const url = this._constructUrl(
      `api/schools/${schoolId}/reset-user-password/batch`
    );

    const expectedResponses = [200, 400, 403, 422];

    const originalRetries = this.retries;

    this.retries = 0;

    const response = await this.requestMaker.makeRequest<{
      updated: string[];
      failed: { userId: string; error: string }[];
    }>(
      url,
      {
        method: 'post',
        body: JSON.stringify({ userIds, newPassword })
      },
      expectedResponses
    );

    if (response.status === 403) {
      throw new ForbiddenAccessToSchoolEntitiesError(schoolId);
    }

    if (response.status === 400) {
      throw new BadRequestError('schoolId', 'missing schoolId path parameter');
    }

    if (response.status === 422) {
      const body = response.json as unknown as {
        pointer: string;
        reason: string;
      };
      throw new BadRequestError(body.pointer, body.reason);
    }

    this.retries = originalRetries;

    return response.json!;
  }

  async batchSaveSchoolEntities(
    schoolId: string,
    entities: {
      teachers?: string[];
      students?: string[];
      classes?: ClassEntity[];
    }
  ) {
    const url = this._constructUrl(`api/admin/schools/${schoolId}/entities`);

    const expectedResponses = [200, 422];
    const response = await this.requestMaker.makeRequest<{
      teachers: TeacherI[];
      students: StudentI[];
      classes: ClassI[];
    }>(
      url,
      { method: 'POST', body: JSON.stringify(entities) },
      expectedResponses
    );

    if (response.status === 422) {
      const body = response.json as unknown as {
        pointer: string;
        reason: string;
      };
      throw new BadRequestError(body.pointer, body.reason);
    }

    return response.json!;
  }

  async writeSchoolInvalidUserEntities({
    schoolId,
    invalidStudents,
    invalidTeachers
  }: {
    schoolId: string;
    invalidTeachers: Omit<InvalidTeacherI, 'type' | 'schoolId'>[];
    invalidStudents: Omit<InvalidStudentI, 'type' | 'schoolId'>[];
  }) {
    const url = this._constructUrl(
      `api/admin/schools/${schoolId}/entities/invalid`
    );

    const expectedResponses = [200, 422];
    const response = await this.requestMaker.makeRequest<{
      invalidTeachers: InvalidTeacherI[];
      invalidStudents: InvalidStudentI[];
    }>(
      url,
      {
        method: 'PUT',
        body: JSON.stringify({ invalidStudents, invalidTeachers })
      },
      expectedResponses
    );

    if (response.status === 422) {
      const body = response.json as unknown as {
        pointer: string;
        reason: string;
      };
      throw new BadRequestError(body.pointer, body.reason);
    }

    return response.json!;
  }

  async batchDeleteSchoolEntities(
    schoolId: string,
    entities: {
      teachers?: string[];
      students?: string[];
      classes?: string[];
      invalidStudentsExternalIds?: string[];
      invalidTeachersExternalIds?: string[];
      deleteSchool?: boolean;
    }
  ) {
    const url = this._constructUrl(`api/admin/schools/${schoolId}/entities`);

    const expectedResponses = [200, 404, 422];
    const response = await this.requestMaker.makeRequest<void>(
      url,
      { method: 'DELETE', body: JSON.stringify(entities) },
      expectedResponses
    );

    if (response.status === 404) {
      throw new SchoolNotFoundError(schoolId);
    }

    if (response.status === 422) {
      const body = response.json as unknown as {
        pointer: string;
        reason: string;
      };
      throw new BadRequestError(body.pointer, body.reason);
    }
  }

  async saveSchool({
    schoolId,
    name,
    externalId
  }: {
    schoolId: string;
    name: string;
    externalId?: string;
  }) {
    const url = this._constructUrl(`api/admin/schools/${schoolId}`);

    const expectedResponses = [200, 403, 422];
    const response = await this.requestMaker.makeRequest<SaveSchoolResponse>(
      url,
      { method: 'POST', body: JSON.stringify({ name, externalId }) },
      expectedResponses
    );

    if (response.status === 403) {
      throw new SchoolInfoBySchoolIdDoesNotExistError(schoolId);
    }

    if (response.status === 422) {
      const body = response.json as unknown as {
        pointer: string;
        reason: string;
      };
      throw new BadRequestError(body.pointer, body.reason);
    }

    return response.json!;
  }

  async saveSchoolByExternalId({
    name,
    externalId,
    urn,
    parentInformationAvailable,
    timeLastSynced,
    syncExpiryDate,
    subscriptionExpiryDate,
    subscriptionChurned,
    externalProvider,
    paidMis
  }: {
    name: string;
    externalId: string;
    urn: string;
    parentInformationAvailable?: boolean;
    timeLastSynced?: string;
    syncExpiryDate?: string;
    subscriptionExpiryDate?: string;
    subscriptionChurned?: boolean;
    externalProvider?: string;
    paidMis?: boolean;
  }) {
    const url = this._constructUrl(`api/admin/schools/external/${externalId}`);

    const expectedResponses = [200, 403, 422, 400];
    const response =
      await this.requestMaker.makeRequest<SaveSchoolByExternalIdResponse>(
        url,
        {
          method: 'POST',
          body: JSON.stringify({
            name,
            urn,
            parentInformationAvailable,
            timeLastSynced,
            syncExpiryDate,
            subscriptionExpiryDate,
            subscriptionChurned,
            externalProvider,
            paidMis
          })
        },
        expectedResponses
      );

    if (response.status === 403) {
      throw new SchoolInfoByUrnDoesNotExistError(urn);
    }

    if (response.status === 422) {
      const body = response.json as unknown as {
        pointer: string;
        reason: string;
      };
      throw new BadRequestError(body.pointer, body.reason);
    }

    if (response.status === 400) {
      const body = response.json as unknown as {
        pointer: string;
        reason: string;
      };
      throw new BadRequestError(body.pointer, body.reason);
    }

    return response.json!;
  }

  async batchFetchSchoolEntitiesByExternalId({
    externalId,
    type,
    limit,
    lastKey
  }: {
    externalId: string;
    type: SchoolEntityTypes;
    limit?: number;
    lastKey?: string;
  }) {
    const url = this._constructUrl(
      `api/admin/schools/external/${externalId}/entities`,
      {
        limit,
        lastKey,
        type
      }
    );

    const expectedResponses = [200, 404, 422];
    const response = await this.requestMaker.makeRequest<{
      items: ClassI[] | StudentI[] | TeacherI[];
      lastKey?: string;
    }>(url, { method: 'GET' }, expectedResponses);

    if (response.status === 404) {
      throw new BatchFetchEntitiesByExternalIdDoesNotExistError();
    }

    if (response.status === 422) {
      const body = response.json as unknown as {
        pointer: string;
        reason: string;
      };
      throw new BadRequestError(body.pointer, body.reason);
    }

    return response.json!;
  }

  async batchDeleteUserSchools(userIds: string[]) {
    const url = this._constructUrl('api/admin/user-schools');
    const expectedResponses = [200, 422];
    const response = await this.requestMaker.makeRequest(
      url,
      { method: 'DELETE', body: JSON.stringify({ userIds }) },
      expectedResponses
    );

    if (response.status === 422) {
      const body = response.json as unknown as {
        pointer: string;
        reason: string;
      };
      throw new BadRequestError(body.pointer, body.reason);
    }

    return response.json;
  }

  async shareQuizzesWithTeachers(
    schoolId: string,
    teacherIds: string[],
    quizId?: string
  ) {
    const url = this._constructUrl(
      `api/schools/${schoolId}/share/quizzes/teachers`
    );

    const expectedResponses = [204, 403, 422];

    const response = await this.requestMaker.makeRequest(
      url,
      {
        method: 'POST',
        body: JSON.stringify({ teacherIds, quizId })
      },
      expectedResponses
    );

    if (response.status === 403) {
      throw new ForbiddenForSchoolError(schoolId);
    }

    if (response.status === 422) {
      const body = response.json as unknown as {
        pointer: string;
        reason: string;
      };
      throw new BadRequestError(body.pointer, body.reason);
    }

    return response.json!;
  }

  _constructUrl(path: string, queryParams?: any) {
    let url = this.url;

    if (!url.endsWith('/')) {
      url += '/';
    }
    url += path;

    if (queryParams && Object.keys(queryParams).length > 0) {
      const queryParamParts = Object.keys(queryParams)
        .filter(param => !!queryParams[param])
        .map(param => `${param}=${encodeURIComponent(queryParams[param])}`);

      return `${url}?${queryParamParts.join('&')}`;
    }

    return url;
  }
}
